import type { CSSProperties } from 'react'
import styled from 'styled-components'

/** Doctor Dashboard UI — Figma Gray/100 */
const PAGE_BG = '#f2f4f7'
/** Figma Gray/300 */
const BORDER_DEFAULT = '#d0d5dd'
/** Figma elevation/02 */
const SHADOW_CARD = `0px 4px 8px -2px rgba(12, 17, 29, 0.1), 0px 2px 4px -2px rgba(12, 17, 29, 0.06)`
/** Figma elevation/00 */
const SHADOW_BUTTON = `0px 1px 2px 0px rgba(12, 17, 29, 0.1)`

const fontStack = `'Public Sans', 'Mulish', sans-serif`

/** Fundo full-bleed (#root usa flex com align-items: center). */
export const PageContainer = styled.div`
  && input,
  && button,
  && select,
  && label,
  && textarea {
    font-family: ${fontStack} !important;
  }

  align-self: stretch;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: ${PAGE_BG};
  /* Header global é position:fixed com 60px — sem isso o título some atrás do header */
  padding-top: 60px;
  min-height: calc(100vh - 60px);
  font-family: ${fontStack};
`

export const PageInner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 32px 24px 48px;
  width: 100%;
  box-sizing: border-box;
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  margin-bottom: 24px;
  width: 100%;

  .back-button {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.primaryDark};
    border: 1px solid ${({ theme }) => theme.colors.primaryDark};
    box-shadow: ${SHADOW_BUTTON};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: opacity 0.2s;

    svg {
      color: #fcfcfd;
    }

    &:hover {
      opacity: 0.92;
    }
  }

  .page-title {
    flex: 1;
    margin: 0;
    font-family: ${fontStack};
    font-size: 30px;
    font-weight: 600;
    line-height: 38px;
    color: ${({ theme }) => theme.colors.gray90};
  }
`

export const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

export const SavedSchedulesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  margin: 0;

  .saved-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    width: 100%;
  }

  .saved-section-description {
    flex: 1;
    min-width: 200px;
    margin: 0;
    font-family: ${fontStack};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.gray70};
  }

  .header-buttons {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    align-items: center;
  }

  .new-period-button {
    min-width: 200px;
    font-family: ${fontStack};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #fcfcfd;
    background: ${({ theme }) => theme.colors.primaryDark};
    border: 1px solid ${({ theme }) => theme.colors.primaryDark};
    border-radius: 8px;
    padding: 10px 16px;
    cursor: pointer;
    box-shadow: ${SHADOW_BUTTON};
    transition: opacity 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover {
      opacity: 0.92;
    }
  }

  .template-button-header {
    font-family: ${fontStack};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.primaryDark};
    background: #fff;
    border: 1px solid ${({ theme }) => theme.colors.primaryDark};
    border-radius: 8px;
    padding: 10px 16px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition:
      background 0.2s,
      color 0.2s;

    &:hover {
      background: ${({ theme }) => theme.colors.primaryLight};
    }

    .template-icon {
      font-size: 20px;
    }
  }

  .saved-title {
    margin: 0;
    width: 100%;
    font-family: ${fontStack};
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.gray90};
  }

  .saved-schedule-card {
    border: 1px solid ${BORDER_DEFAULT};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: #fff;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 0;
    border: none;
    background: transparent;

    .period-dates {
      flex: 1;
      margin: 0;
      font-family: ${fontStack};
      font-size: 18px;
      font-weight: 500;
      line-height: 24px;
      color: ${({ theme }) => theme.colors.gray90};
    }
  }

  .card-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .action-button {
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;

    img {
      width: 24px;
      height: 24px;
    }

    &:hover {
      opacity: 0.75;
    }
  }

  .week-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px;
    width: 100%;

    @media screen and (max-width: 900px) {
      grid-template-columns: repeat(4, 1fr);
    }

    @media screen and (max-width: 600px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: 400px) {
      grid-template-columns: 1fr;
    }
  }

  .day-column {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;

    .day-name {
      font-family: ${fontStack};
      font-size: 18px;
      font-weight: 500;
      line-height: 24px;
      color: ${({ theme }) => theme.colors.gray90};
    }

    .day-hours {
      font-family: ${fontStack};
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      color: ${({ theme }) => theme.colors.gray60};
    }
  }
`

export const DateRangeSection = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  margin: 0;

  .date-range-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 16px;
  }

  .section-title {
    margin: 0 0 8px 0;
    font-family: ${fontStack};
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.gray90};
  }

  .section-subtitle {
    display: inline-block;
    margin: 0;
    padding: 4px 8px;
    border-radius: 16px;
    font-family: ${fontStack};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #e27a41;
    background: #fef3e5;
  }

  .template-button {
    font-family: ${fontStack};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.primaryDark};
    background: #fff;
    border: 1px solid ${({ theme }) => theme.colors.primaryDark};
    border-radius: 8px;
    padding: 10px 16px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: background 0.2s;

    &:hover {
      background: ${({ theme }) => theme.colors.primaryLight};
    }

    .template-icon {
      font-size: 20px;
    }
  }

  .date-inputs {
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: stretch;
  }

  .input-group {
    display: flex;
    flex-direction: column;
    width: 100%;

    label {
      margin-bottom: 8px;
      font-family: ${fontStack};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.gray60};
    }

    select {
      padding: 10px 14px;
      border: 1px solid ${BORDER_DEFAULT};
      border-radius: 8px;
      font-family: ${fontStack};
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.colors.gray90};
      background: #fff;
      outline: none;
      cursor: pointer;
      min-width: 200px;

      &:focus {
        border-color: ${({ theme }) => theme.colors.primaryDark};
      }
    }
  }

  .input-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fff;
    border: 1px solid ${BORDER_DEFAULT};
    border-radius: 8px;
    padding: 10px 14px;
    min-width: 180px;

    svg {
      color: ${({ theme }) => theme.colors.gray60};
      flex-shrink: 0;
    }

    input {
      border: none;
      outline: none;
      font-family: ${fontStack};
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.colors.gray90};
      width: 100%;
      background: transparent;

      &::placeholder {
        color: ${({ theme }) => theme.colors.gray40};
      }
    }
  }

  .date-error {
    margin: 12px 0 0 0;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid #fecdd3;
    background: #fff5f6;
    font-family: ${fontStack};
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.error};
  }
`

export const WeekScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media screen and (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

export const DayCard = styled.div<{ isEmpty?: boolean }>`
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${BORDER_DEFAULT};

  .day-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .day-name {
    font-family: ${fontStack};
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.gray90};
  }

  .add-time-button {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    font-family: ${fontStack};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.primaryDark};
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover {
      text-decoration: underline;
    }
  }

  .no-schedule-text {
    margin: 0;
    padding: 16px 0;
    text-align: center;
    font-family: ${fontStack};
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.gray60};
  }

  .time-slot {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .time-input-group {
    flex: 1;

    label {
      display: block;
      margin-bottom: 6px;
      font-family: ${fontStack};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.gray60};
    }
  }

  .time-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1px solid ${BORDER_DEFAULT};
    border-radius: 8px;
    padding: 10px 12px;

    svg {
      color: ${({ theme }) => theme.colors.gray60};
    }

    input {
      border: none;
      outline: none;
      font-family: ${fontStack};
      font-size: 14px;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.gray90};
      flex: 1;
      background: transparent;
      width: 72px;

      &::-webkit-calendar-picker-indicator {
        display: none;
      }
    }
  }

  .remove-slot-button {
    background: #fff;
    border: 1px solid ${BORDER_DEFAULT};
    border-radius: 8px;
    padding: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
      background 0.2s,
      border-color 0.2s;

    svg {
      color: ${({ theme }) => theme.colors.error};
    }

    &:hover {
      background: #fff5f6;
      border-color: ${({ theme }) => theme.colors.error};
    }
  }
`

export const ExceptionsSection = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;

  .exceptions-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 16px;

    h3 {
      margin: 0 0 4px 0;
      font-family: ${fontStack};
      font-size: 18px;
      font-weight: 500;
      line-height: 24px;
      color: ${({ theme }) => theme.colors.gray90};
    }

    p {
      margin: 0;
      font-family: ${fontStack};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.gray60};
    }
  }

  .add-exception-button {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    font-family: ${fontStack};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.primaryDark};
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover {
      text-decoration: underline;
    }
  }

  .no-exceptions-text {
    margin: 0;
    padding: 16px 0;
    text-align: center;
    font-family: ${fontStack};
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.gray60};
  }

  .exceptions-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .exception-item-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid ${BORDER_DEFAULT};
  }

  .exception-reason-row {
    width: 100%;
  }

  .exception-fields-row {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .exception-input-group {
    display: flex;
    flex-direction: column;
    min-width: 140px;

    &.full-width {
      width: 100%;
    }

    label {
      display: block;
      margin-bottom: 6px;
      font-family: ${fontStack};
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.gray60};
    }

    .reason-input {
      width: 100%;
      padding: 10px 14px;
      border: 1px solid ${BORDER_DEFAULT};
      border-radius: 8px;
      font-family: ${fontStack};
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.colors.gray90};
      outline: none;
      background: #fff;

      &::placeholder {
        color: ${({ theme }) => theme.colors.gray40};
      }

      &:focus {
        border-color: ${({ theme }) => theme.colors.primaryDark};
      }
    }
  }

  .exception-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1px solid ${BORDER_DEFAULT};
    border-radius: 8px;
    padding: 10px 12px;

    svg {
      color: ${({ theme }) => theme.colors.gray60};
      flex-shrink: 0;
    }

    input {
      border: none;
      outline: none;
      font-family: ${fontStack};
      font-size: 14px;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.gray90};
      width: 100%;
      background: transparent;

      &::placeholder {
        color: ${({ theme }) => theme.colors.gray40};
      }

      &::-webkit-calendar-picker-indicator {
        display: none;
      }
    }
  }

  .remove-exception-button {
    background: #fff;
    border: 1px solid ${BORDER_DEFAULT};
    border-radius: 8px;
    padding: 10px;
    height: 42px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
      background 0.2s,
      border-color 0.2s;

    svg {
      color: ${({ theme }) => theme.colors.error};
    }

    &:hover {
      background: #fff5f6;
      border-color: ${({ theme }) => theme.colors.error};
    }
  }
`

export const PageFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 24px;
  margin-top: 8px;
  border-top: 1px solid ${BORDER_DEFAULT};

  .cancel-button {
    padding: 10px 18px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.primaryDark};
    background: #fff;
    font-family: ${fontStack};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.primaryDark};
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: ${({ theme }) => theme.colors.primaryLight};
    }
  }

  .submit-button {
    padding: 10px 16px;
    border: 1px solid ${({ theme }) => theme.colors.primaryDark};
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.primaryDark};
    box-shadow: ${SHADOW_BUTTON};
    font-family: ${fontStack};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #fcfcfd;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.92;
    }
  }
`

export const DeleteModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

export const DeleteModal = styled.div`
  width: 100%;
  max-width: 576px;
  background: #fcfcfd;
  border-radius: 8px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;

  .modal-title-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    text-align: center;
    width: 100%;
  }

  h3 {
    margin: 0;
    font-family: ${fontStack};
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    color: ${({ theme }) => theme.colors.gray90};
  }

  p {
    margin: 0;
    font-family: ${fontStack};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.gray60};
    text-align: center;
  }

  .modal-actions {
    display: flex;
    gap: 16px;
    width: 100%;
  }

  .cancel-button {
    flex: 1;
    padding: 10px 18px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.primaryDark};
    background: transparent;
    font-family: ${fontStack};
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: ${({ theme }) => theme.colors.primaryDark};
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: ${({ theme }) => theme.colors.primaryLight};
    }
  }

  .delete-button {
    flex: 1;
    padding: 10px 18px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.error};
    background: ${({ theme }) => theme.colors.error};
    box-shadow: ${SHADOW_BUTTON};
    font-family: ${fontStack};
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: #fcfcfd;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.92;
    }
  }
`

export const mainCardSurfaceStyle: CSSProperties = {
  padding: '24px',
  borderRadius: '8px',
  boxShadow: SHADOW_CARD,
  background: '#ffffff',
  border: 'none',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
}
